import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft,
  Calendar,
  Target,
  BookOpen,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Zap,
  RefreshCcw,
  ChevronRight,
  Lightbulb,
  Lock,
  LogIn,
  Trophy,
  Star,
  Flame,
  GraduationCap,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

const MasteryBadge = ({ level }) => {
  const config = {
    "Mastered": { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: Trophy },
    "Proficient": { color: "bg-blue-100 text-blue-700 border-blue-200", icon: Star },
    "Learning": { color: "bg-amber-100 text-amber-700 border-amber-200", icon: TrendingUp },
    "Needs Focus": { color: "bg-rose-100 text-rose-700 border-rose-200", icon: AlertTriangle }
  };
  const { color, icon: Icon } = config[level] || config["Learning"];
  
  return (
    <Badge className={`${color} border font-medium flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {level}
    </Badge>
  );
};

const DayCard = ({ day, schedule, onStartQuiz }) => {
  const isWeekend = day === "Saturday" || day === "Sunday";
  const bgColor = isWeekend ? "bg-gradient-to-br from-violet-50 to-purple-50" : "bg-white";
  
  return (
    <motion.div 
      className={`${bgColor} rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow`}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-900">{day}</h4>
        {isWeekend && (
          <Badge variant="outline" className="text-violet-600 border-violet-200">
            Review Day
          </Badge>
        )}
      </div>
      
      {schedule.focus_areas.map((area, idx) => (
        <div key={idx} className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-slate-700">{area.category}</span>
            <Badge variant="outline" className="text-xs">{area.type}</Badge>
          </div>
          <p className="text-xs text-slate-500">{area.reason}</p>
        </div>
      ))}
      
      {schedule.recommended_quizzes.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-2">Recommended:</p>
          {schedule.recommended_quizzes.slice(0, 2).map((quiz, idx) => (
            <button
              key={idx}
              onClick={() => onStartQuiz(quiz.id)}
              className="w-full text-left p-2 rounded-lg bg-slate-50 hover:bg-teal-50 transition-colors mb-2 group"
              data-testid={`quiz-btn-${quiz.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700 group-hover:text-teal-700">{quiz.title}</span>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">{quiz.difficulty}</Badge>
                <span className="text-xs text-slate-400">{quiz.time_limit} min</span>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {schedule.study_tips.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1 mb-2">
            <Lightbulb className="w-3 h-3 text-amber-500" />
            <span className="text-xs font-medium text-slate-500">Tips</span>
          </div>
          {schedule.study_tips.map((tip, idx) => (
            <p key={idx} className="text-xs text-slate-600 mb-1">• {tip}</p>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default function StudyPlanPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isPremium, loading: authLoading, authChecked } = useAuth();
  const [studyPlan, setStudyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (isAuthenticated) {
      fetchStudyPlan();
    }
  }, [isAuthenticated]);

  const fetchStudyPlan = async () => {
    try {
      const response = await axios.get(`${API}/study-plan`);
      setStudyPlan(response.data);
    } catch (error) {
      toast.error("Failed to load study plan");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = (quizId) => {
    if (quizId === "weak-areas-practice") {
      navigate("/analytics");
    } else {
      navigate(`/quiz/${quizId}`);
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-amber-600";
    return "text-rose-600";
  };

  const getReadinessGradient = (score) => {
    if (score >= 80) return "from-emerald-500 to-teal-500";
    if (score >= 60) return "from-blue-500 to-cyan-500";
    if (score >= 40) return "from-amber-500 to-orange-500";
    return "from-rose-500 to-pink-500";
  };

  // Show login required screen if not authenticated
  if (authChecked && !authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Login Required</h1>
            <p className="text-slate-600 mb-6">
              Sign in to access your personalized study plan and track your progress.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In / Create Account
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasData = studyPlan?.overall_stats?.total_questions_practiced > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-xl tracking-tight">Study Plan</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                onClick={fetchStudyPlan}
                data-testid="refresh-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={() => navigate("/quizzes")}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="practice-btn"
              >
                Start Practicing
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!hasData ? (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <GraduationCap className="w-20 h-20 mx-auto text-slate-300 mb-6" />
            <h2 className="text-2xl font-bold mb-2 text-slate-900" data-testid="no-data-title">Start Your Journey</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Complete some quizzes first, and we&apos;ll create a personalized study plan tailored to your strengths and areas for improvement.
            </p>
            <Button 
              onClick={() => navigate("/quizzes")}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="start-first-quiz-btn"
            >
              Take Your First Quiz
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Exam Readiness Banner */}
            <motion.div 
              className={`rounded-2xl bg-gradient-to-r ${getReadinessGradient(studyPlan.overall_stats.readiness_score)} p-6 mb-8 text-white`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-6 h-6" />
                    <span className="text-sm font-medium opacity-90">Exam Readiness</span>
                  </div>
                  <h2 className="text-3xl font-bold mb-1" data-testid="readiness-status">
                    {studyPlan.overall_stats.exam_readiness}
                  </h2>
                  <p className="text-sm opacity-80">
                    {studyPlan.overall_stats.overall_accuracy}% overall accuracy • {studyPlan.overall_stats.categories_attempted}/{studyPlan.overall_stats.total_categories} categories covered
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold" data-testid="readiness-score">
                    {studyPlan.overall_stats.readiness_score}%
                  </div>
                  <p className="text-sm opacity-80">Readiness Score</p>
                </div>
              </div>
              <div className="mt-4">
                <Progress 
                  value={studyPlan.overall_stats.readiness_score} 
                  className="h-2 bg-white/20"
                />
              </div>
            </motion.div>

            {/* Navigation Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {["overview", "schedule", "categories"].map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? "default" : "outline"}
                  onClick={() => setActiveTab(tab)}
                  className={activeTab === tab ? "bg-teal-600 hover:bg-teal-700" : ""}
                  data-testid={`tab-${tab}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {/* Key Recommendations */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Key Recommendations
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {studyPlan.key_recommendations.map((rec, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-xl border ${
                          rec.type === "priority" ? "bg-rose-50 border-rose-200" :
                          rec.type === "explore" ? "bg-blue-50 border-blue-200" :
                          rec.type === "challenge" ? "bg-violet-50 border-violet-200" :
                          "bg-amber-50 border-amber-200"
                        }`}
                        data-testid={`recommendation-${idx}`}
                      >
                        <h4 className="font-medium text-slate-900 mb-1">{rec.title}</h4>
                        <p className="text-sm text-slate-600 mb-2">{rec.description}</p>
                        <p className="text-sm font-medium text-slate-700">
                          <ArrowRight className="w-4 h-4 inline mr-1" />
                          {rec.action}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-teal-600" />
                      <span className="text-sm text-slate-500">Questions Practiced</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="total-questions">
                      {studyPlan.overall_stats.total_questions_practiced}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-slate-500">Accuracy</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="accuracy">
                      {studyPlan.overall_stats.overall_accuracy}%
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm text-slate-500">Categories Covered</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="categories-covered">
                      {studyPlan.overall_stats.categories_attempted}/{studyPlan.overall_stats.total_categories}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-rose-600" />
                      <span className="text-sm text-slate-500">Missed Questions</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="missed-count">
                      {studyPlan.missed_questions_count}
                    </p>
                  </div>
                </div>

                {/* Untested Categories Alert */}
                {studyPlan.untested_categories.length > 0 && (
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800 mb-1">Categories Not Yet Practiced</h4>
                        <p className="text-sm text-amber-700 mb-2">
                          You haven&apos;t explored these topics yet. Consider adding them to your study routine.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {studyPlan.untested_categories.map((cat, idx) => (
                            <Badge key={idx} variant="outline" className="bg-white border-amber-300 text-amber-700">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Schedule Tab */}
            {activeTab === "schedule" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-teal-600" />
                      Your Weekly Study Schedule
                    </h3>
                    <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                      Personalized for You
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    This schedule is generated based on your performance. Focus on weak areas during weekdays and use weekends for review and mock exams.
                  </p>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {studyPlan.weekly_schedule.map((schedule, idx) => (
                      <DayCard 
                        key={idx} 
                        day={schedule.day} 
                        schedule={schedule} 
                        onStartQuiz={handleStartQuiz}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Categories Tab */}
            {activeTab === "categories" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-teal-600" />
                    Category Performance
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">
                    Your performance across different BCBA exam topic areas, sorted by priority.
                  </p>
                  
                  <div className="space-y-4">
                    {studyPlan.category_analysis.map((cat, idx) => (
                      <div 
                        key={idx}
                        className="p-4 rounded-xl border border-slate-200 hover:border-teal-200 transition-colors"
                        data-testid={`category-${idx}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-900">{cat.category}</span>
                            <MasteryBadge level={cat.mastery_level} />
                          </div>
                          <span className={`text-xl font-bold ${
                            cat.accuracy >= 85 ? "text-emerald-600" :
                            cat.accuracy >= 70 ? "text-blue-600" :
                            cat.accuracy >= 55 ? "text-amber-600" :
                            "text-rose-600"
                          }`}>
                            {cat.accuracy}%
                          </span>
                        </div>
                        <Progress 
                          value={cat.accuracy} 
                          className="h-2 mb-3"
                        />
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <span>{cat.correct_answers}/{cat.questions_attempted} correct</span>
                          <span>{cat.practice_sessions} practice sessions</span>
                          <span>Avg {cat.avg_time_per_session} min/session</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
